import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import { QUERY_KEYS } from './queryKeys';

export interface TechnicalAssessment {
    id: number;
    title: string;
    date: string;
    technician_name?: string;
    priority: 'urgent' | 'high' | 'medium' | 'low';
    status: 'draft' | 'submitted' | 'approved' | 'rejected';
    status_display: string;
}

const fetchTechnicalAssessments = async (): Promise<TechnicalAssessment[]> => {
    const { data } = await api.get(API_ROUTES.technicalAssessments);
    return data;
};

export const useTechnicalAssessmentsList = () => {
    return useQuery({
        queryKey: QUERY_KEYS.technicalAssessments.all,
        queryFn: fetchTechnicalAssessments,
    });
};

const createTechnicalAssessment = async (data: any) => {
    const { data: response } = await api.post(API_ROUTES.technicalAssessments, data);
    return response;
};

export const useCreateTechnicalAssessment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createTechnicalAssessment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.technicalAssessments.all });
        },
    });
};

const fetchTechnicalAssessmentDetail = async (id: string | number) => {
    const { data } = await api.get(API_ROUTES.technicalAssessmentDetail(id));
    return data;
};

export const useTechnicalAssessmentDetail = (id: string | number) => {
    return useQuery({
        queryKey: QUERY_KEYS.technicalAssessments.detail(id),
        queryFn: () => fetchTechnicalAssessmentDetail(id),
        enabled: !!id,
    });
};

const updateTechnicalAssessment = async ({ id, data }: { id: string | number; data: any }) => {
    const { data: response } = await api.put(API_ROUTES.technicalAssessmentDetail(id), data);
    return response;
};

export const useUpdateTechnicalAssessment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateTechnicalAssessment,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.technicalAssessments.all });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.technicalAssessments.detail(variables.id) });
        },
    });
};
